"use client";

import { useEffect, useState, useCallback } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useSession, signOut } from "next-auth/react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { setAuthToken, getAuthToken } from "@/lib/api";
import {
  LogOut,
  FileText,
  MessageSquare,
  Calendar,
  LayoutDashboard,
  Wallet,
  Landmark,
  Menu,
  X,
  Sparkles,
} from "lucide-react";

const AUTH_PAGES = ["/client/login", "/client/register", "/client/forgot-password"];

const NAV_ITEMS = [
  { href: "/client/dashboard", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { href: "/client/transactions", label: "Transactions", icon: FileText, exact: true },
  { href: "/client/bank-connections", label: "Banks", icon: Landmark, exact: true },
  { href: "/client/tickets", label: "Tickets", icon: MessageSquare, exact: false },
  { href: "/client/appointments", label: "Appointments", icon: Calendar, exact: true },
  { href: "/client/credits", label: "Credits", icon: Wallet, exact: true },
];

function ClientHeader() {
  const router = useRouter();
  const pathname = usePathname();
  const isInIframe = typeof window !== "undefined" && window.self !== window.top;
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    setAuthToken(null);
    await signOut({ redirect: false });
    router.push("/client/login");
  };

  const isActive = (item: typeof NAV_ITEMS[0]) =>
    item.exact ? pathname === item.href : pathname.startsWith(item.href);

  return (
    <header className="glass border-b border-border/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex h-16 items-center justify-between">
          {/* Left: Logo + Badge */}
          <div className="flex items-center gap-3">
            <Link href="/client/dashboard" className="flex items-center h-full">
              {!isInIframe && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src="/billance-ledger-logo.png"
                  alt="Billance Ledger"
                  className="h-12 w-auto"
                />
              )}
            </Link>
            <span className="hidden sm:inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
              Client Portal
            </span>
          </div>

          {/* Center: Nav (desktop) */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_ITEMS.map((item) => {
              const active = isActive(item);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`px-3.5 py-2 text-sm font-medium rounded-full flex items-center gap-2 transition-all duration-200 ${
                    active
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Right: Logout + Mobile toggle */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-muted-foreground hover:text-foreground gap-2"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="md:hidden pb-4 pt-2 border-t border-border/50">
            <nav className="flex flex-col gap-1">
              {NAV_ITEMS.map((item) => {
                const active = isActive(item);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={`px-4 py-2.5 text-sm font-medium rounded-lg flex items-center gap-3 transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted"
                    }`}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}

export default function ClientLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const { data: session, status } = useSession();
  const [isReady, setIsReady] = useState(false);

  const isAuthPage = AUTH_PAGES.some((p) => pathname.startsWith(p));

  // Support token-from-hash for iframe embedding (widget integration)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.startsWith("#token=")) {
      const token = hash.slice(7);
      if (token) {
        setAuthToken(token);
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
    }
    // Listen for token messages from parent (widget)
    const handleTokenMessage = (event: MessageEvent) => {
      if (event.data?.type === "BOOKKEEPING_WIDGET_TOKEN" && event.data.token) {
        setAuthToken(event.data.token);
      }
    };
    window.addEventListener("message", handleTokenMessage);
    return () => window.removeEventListener("message", handleTokenMessage);
  }, []);

  // Send resize messages to parent iframe (if embedded)
  useEffect(() => {
    if (window.self === window.top) return; // not in iframe
    const sendHeight = () => {
      window.parent.postMessage(
        { type: "BOOKKEEPING_WIDGET_RESIZE", height: document.documentElement.scrollHeight },
        "*"
      );
    };
    const observer = new ResizeObserver(sendHeight);
    observer.observe(document.body);
    sendHeight();
    return () => observer.disconnect();
  }, [isReady]);

  useEffect(() => {
    if (status === "authenticated" && session?.backendAccessToken) {
      setAuthToken(session.backendAccessToken);
      setIsReady(true);
    } else if (status === "unauthenticated") {
      const existingToken = getAuthToken();
      if (existingToken) {
        setIsReady(true);
      } else {
        setAuthToken(null);
        if (!isAuthPage) {
          router.push("/client/login");
        } else {
          setIsReady(true);
        }
      }
    } else if (status === "loading") {
      const existingToken = getAuthToken();
      if (existingToken) {
        setIsReady(true);
      }
    }
  }, [status, session, router, isAuthPage]);

  if (isAuthPage) {
    return <>{children}</>;
  }

  if (status === "loading" && !isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-[oklch(0.60_0.22_290)] flex items-center justify-center animate-pulse">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (status === "unauthenticated") {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <ClientHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">{children}</main>
    </div>
  );
}
